/**
 * MusicSearch — Spotify-style single search field that autocompletes via
 * the backend /api/music/search proxy (iTunes). Selecting a suggestion
 * populates the request payload; guest just presses Submit.
 */
import { useEffect, useRef, useState } from "react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { Search, Music as MusicIcon, X } from "lucide-react";
import { T } from "../lib/i18n";

export default function MusicSearch({ onSubmitted }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const seq = useRef(0);

  // Debounce queries; keep only the latest response
  useEffect(() => {
    if (selected || !q.trim() || q.trim().length < 2) {
      setItems([]);
      setSearchFailed(false);
      return;
    }
    const my = ++seq.current;
    setLoading(true);
    setSearchFailed(false);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/music/search`, { params: { q: q.trim(), limit: 10 } });
        if (my === seq.current) setItems(data);
      } catch (err) {
        console.debug("music: search failed", err);
        if (my === seq.current) setSearchFailed(true);
      }
      finally { if (my === seq.current) setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, selected]);

  const pick = (item) => {
    setSelected(item);
    setItems([]);
    setQ(`${item.title} — ${item.artist}`);
  };

  const clearPick = () => {
    setSelected(null);
    setQ("");
  };

  const submit = async () => {
    if (!selected && !q.trim()) return;
    setSubmitting(true);
    try {
      const payload = selected
        ? {
            title: selected.title,
            artist: selected.artist,
            album: selected.album,
            artwork_url: selected.artwork,
            track_id: selected.track_id,
          }
        : { title: q.trim(), artist: "" };
      await api.post("/music", payload);
      toast.success(T("Song requested"));
      clearPick();
      onSubmitted && onSubmitted();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="music-search">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-motif-muted pointer-events-none" />
        <input
          className="input-motif"
          style={{ paddingLeft: "2.25rem", paddingRight: selected ? "2.5rem" : undefined }}
          placeholder={T("Search songs, artist, album or genre...")}
          value={q}
          onChange={(e) => { setQ(e.target.value); setSelected(null); }}
          data-testid="music-search-input"
          aria-label="Search songs"
        />
        {selected && (
          <button
            onClick={clearPick}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center"
            style={{ background: "var(--motif-accent)" }}
            aria-label="Clear selection"
            data-testid="music-search-clear"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {loading && !selected && (
        <div className="text-[10px] tracking-luxe text-motif-muted">{T("searching…")}</div>
      )}

      {searchFailed && !selected && !loading && (
        <div className="text-[11px] text-motif-muted italic" data-testid="music-search-error">
          {T("Search is unavailable right now — you can still type your song and request it.")}
        </div>
      )}

      {!selected && items.length > 0 && (
        <ul className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="music-search-results">
          {items.map((it) => (
            <li key={it.track_id || `${it.title}-${it.artist}`}>
              <button
                onClick={() => pick(it)}
                className="w-full flex items-center gap-2.5 p-2 rounded-lg text-left transition hover:opacity-90"
                style={{ background: "var(--motif-accent)" }}
                data-testid={`music-search-result-${it.track_id}`}
              >
                {it.artwork ? (
                  <img src={it.artwork} alt="" loading="lazy" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0" style={{ background: "var(--motif-surface)" }}>
                    <MusicIcon className="w-4 h-4 text-motif-muted" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-motif truncate">{it.title}</div>
                  <div className="text-[11px] text-motif-muted truncate">{it.artist}{it.album ? ` · ${it.album}` : ""}</div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {selected && (
        <div
          className="flex items-center gap-2.5 p-2 rounded-lg"
          style={{ background: "var(--motif-accent)" }}
          data-testid="music-search-selected"
        >
          {selected.artwork ? (
            <img src={selected.artwork} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
          ) : (
            <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0" style={{ background: "var(--motif-surface)" }}>
              <MusicIcon className="w-4 h-4 text-motif-muted" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm text-motif truncate">{selected.title}</div>
            <div className="text-[11px] text-motif-muted truncate">{selected.artist}{selected.album ? ` · ${selected.album}` : ""}</div>
          </div>
        </div>
      )}

      <button
        onClick={submit}
        disabled={submitting || (!selected && q.trim().length < 2)}
        className="btn-primary w-full text-sm disabled:opacity-50"
        data-testid="music-search-submit"
      >
        {submitting ? T("Requesting…") : T("Request Song")}
      </button>
    </div>
  );
}
